/**
 * Carousel Component
 *
 * Displays a carousel of recipe cards using Flickity.
 * Each card shows a recipe's cover image and name.
 * Clicking a card navigates to the recipe's detail page.
 */

import React, { useEffect, useRef, useState } from "react";
import Flickity from "react-flickity-component";
import "flickity/css/flickity.css";
import { Box, Typography, GlobalStyles, useTheme } from "@mui/material";
import { getStaticAssetPath } from "../../utils/staticAssets";
import { FetchedRecipe } from "../../types/recipe";
import { ROUTES } from "../../constants/routes";

/**
 * Props for the Carousel component.
 * @property recipes - Array of recipes to display.
 * @property options - Flickity options (optional).
 * @property className - Additional class name for the carousel (optional).
 */
interface CarouselProps {
  recipes: FetchedRecipe[];
  options?: object;
  className?: string;
}

/**
 * Default Flickity options for the carousel.
 */
const defaultOptions = {
  prevNextButtons: false,
  autoPlay: 3000,
  wrapAround: true,
  pageDots: true,
  cellAlign: "left",
  contain: true,
  resize: true,
  imagesLoaded: true,
};

// @ts-ignore
const FlickityComponent = Flickity as unknown as React.FC<any>;

/**
 * Carousel functional component.
 * @param recipes - Array of recipes to display.
 * @param options - Flickity options (optional).
 * @param className - Additional class name for the carousel (optional).
 */
const Carousel: React.FC<CarouselProps> = ({ recipes, options, className }) => {
  const theme = useTheme();
  const flickityRef = useRef<any>(null);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);

  // Preload all images before initializing Flickity
  useEffect(() => {
    const imageUrls = recipes
      .filter((recipe) => recipe.data.coverImage?.source)
      .map((recipe) => getStaticAssetPath(recipe.data.coverImage!.source));

    if (imageUrls.length === 0) {
      setImagesPreloaded(true);
      return;
    }

    let loadedCount = 0;
    const totalImages = imageUrls.length;

    imageUrls.forEach((url) => {
      const img = new Image();
      img.onload = img.onerror = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setImagesPreloaded(true);
        }
      };
      img.src = url;
    });
  }, [recipes]);

  useEffect(() => {
    if (!imagesPreloaded) return;

    const timer = setTimeout(() => {
      if (flickityRef.current && flickityRef.current.flickity) {
        flickityRef.current.flickity.resize();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [recipes, imagesPreloaded]);

  useEffect(() => {
    const handleResize = () => {
      if (flickityRef.current && flickityRef.current.flickity) {
        flickityRef.current.flickity.resize();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Don't render Flickity until images are preloaded
  if (!imagesPreloaded) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: 270,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.palette.grey[50],
          borderRadius: 2,
        }}
      />
    );
  }

  return (
    <>
      {/* Global styles for Flickity page dots */}
      <GlobalStyles
        styles={{
          ".flickity-page-dots": {
            position: "absolute",
            right: 24,
            bottom: 16,
            left: "auto",
            margin: 0,
            display: "flex",
            justifyContent: "flex-end",
            width: "auto",
            zIndex: 2,
          },
          ".flickity-page-dots .dot": {
            background: theme.palette.materiom?.seaweed || "#609690",
            opacity: 0.4,
            margin: "0 4px",
            width: 12,
            height: 12,
          },
          ".flickity-page-dots .dot.is-selected": {
            opacity: 1,
            background: theme.palette.materiom?.seaweed || "#609690",
          },
        }}
      />
      <FlickityComponent
        ref={flickityRef}
        key={`${recipes.map((r) => r._id).join("-")}-${imagesPreloaded}`}
        className={className || "carousel"}
        elementType="div"
        options={{ ...defaultOptions, ...options, imagesLoaded: false }}
        disableImagesLoaded={true}
        reloadOnUpdate
      >
        {recipes.map((recipe) => (
          <Box
            key={recipe._id}
            component="a"
            href="#"
            /**
             * When a recipe card is clicked, prevent default anchor behavior
             * and navigate to the recipe detail page for the selected recipe.
             */
            onClick={(e) => {
              e.preventDefault();
              window.open(
                `${ROUTES.RECIPE_DETAIL}/${recipe._id}`,
                "_blank",
                "noopener,noreferrer"
              );
            }}
            sx={{
              width: "100%",
              minHeight: 270,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              borderRadius: 2,
              overflow: "hidden",
              backgroundImage:
                recipe.data.coverImage?.source &&
                `url(${getStaticAssetPath(recipe.data.coverImage.source)})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              position: "relative",
              textDecoration: "none",
              transition: "box-shadow 0.3s",
              cursor: "pointer",
            }}
          >
            {/* Overlay bar at the top with recipe name */}
            <Box
              sx={{
                width: "100%",
                background: "rgba(90, 90, 90, 0.7)",
                color: "#fff",
                fontWeight: "bold",
                fontStyle: "italic",
                fontSize: "1.5rem",
                px: 3,
                py: 2,
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
                textShadow: "0 2px 8px rgba(0,0,0,0.2)",
                display: "flex",
                alignItems: "center",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontStyle: "italic",
                  textAlign: "left",
                }}
              >
                {recipe.data.name}
              </Typography>
            </Box>
          </Box>
        ))}
      </FlickityComponent>
    </>
  );
};

export default Carousel;
